/**
 * Central "Request Consultation" primitive. Every button that starts the
 * consultation flow on the site routes through this component so we can:
 *   - point every button at the single canonical /request-consultation page
 *   - track a single "consultation_click" analytics event with a `location`
 *     label so we can measure which surface converts best
 *
 * All consultation flow begins with a lead form. There is no external booking
 * platform, no calendar embed, no third-party redirect.
 *
 * Props:
 *   - location: string  (required — analytics label, e.g. 'header-nav', 'article-footer', 'sticky-cta')
 *   - variant:  'primary' | 'secondary' | 'ghost' | 'gold'
 *   - size:     'sm' | 'md' | 'lg'
 *   - children: optional override label (defaults to "Request Consultation")
 *   - testId:   optional data-testid override
 *   - fullWidth: bool
 */
import { Link } from "react-router-dom";
import { Calendar } from "lucide-react";
import { trackEvent } from "../lib/analytics";

const CONSULTATION_URL = "/request-consultation";

const SIZES = {
  sm: "text-sm px-3.5 py-1.5 gap-1.5",
  md: "text-sm px-4 py-2 gap-2",
  lg: "text-base px-5 py-3 gap-2",
};

const VARIANTS = {
  primary:   "bg-[color:var(--vv-teal)] text-white hover:bg-[color:var(--vv-teal-hover)]",
  gold:      "bg-[color:var(--vv-gold-deep)] text-white hover:opacity-90",
  secondary: "bg-white text-[color:var(--vv-ink)] border border-[color:var(--vv-teal)] hover:bg-[color:var(--vv-teal)]/5",
  ghost:     "text-[color:var(--vv-teal)] hover:bg-[color:var(--vv-teal)]/5",
};

export function ScheduleConsultationCTA({
  location,
  variant = "primary",
  size = "md",
  children,
  testId,
  fullWidth = false,
  showIcon = true,
  className = "",
}) {
  if (!location) {
    // Fail loud in dev so we always have an analytics label.
    // eslint-disable-next-line no-console
    console.warn("ScheduleConsultationCTA is missing a `location` prop");
  }
  const label = children || "Request Consultation";
  const testid = testId || `schedule-consultation-${location || "unknown"}`;
  const handleClick = () => {
    try {
      trackEvent("consultation_click", { location });
    } catch (_) {
      /* analytics is best-effort */
    }
  };
  return (
    <Link
      to={CONSULTATION_URL}
      onClick={handleClick}
      data-testid={testid}
      aria-label={label}
      className={[
        "inline-flex items-center justify-center rounded-full font-semibold transition-colors",
        SIZES[size],
        VARIANTS[variant],
        fullWidth ? "w-full" : "",
        className,
      ].filter(Boolean).join(" ")}
    >
      {showIcon ? <Calendar size={size === "lg" ? 18 : 15} /> : null}
      {label}
    </Link>
  );
}

export default ScheduleConsultationCTA;
